import { Vec3 } from '@/core/math/Vec3';
import { DynamicFrame, StaticFrame } from '@/frames/ReferenceFrame';
import { ReferenceFrameManager } from '@/frames/ReferenceFrameManager';
import { CelestialBodyDef, StarDef } from './BodyDef';
import { CelestialBody } from './CelestialBody';
import { CYRENE } from './data/star';
import { MOONS } from './data/moons';
import { HOME_PLANET_ID, PLANETS } from './data/planets';

export const ROOT_FRAME_ID = 'sysroot';

export interface SolarSystemConfig {
  /** Master seed. Every procedural subsystem derives from this. */
  seed: number;
  star: StarDef;
  bodies: CelestialBodyDef[];
}

export const DEFAULT_SYSTEM_CONFIG: SolarSystemConfig = {
  seed: 0x5f4d3c2b,
  star: CYRENE,
  bodies: [...PLANETS, ...MOONS],
};

/**
 * The persistent solar system.
 *
 * Construction does three things and nothing else:
 *   1. builds `CelestialBody` runtime objects from the definitions,
 *   2. links the parent/child hierarchy and computes spheres of influence,
 *   3. registers one inertial and one body-fixed reference frame per body.
 *
 * After that the system is immutable. Nothing in the game can move a planet.
 */
export class SolarSystem {
  readonly seed: number;
  readonly frames: ReferenceFrameManager;
  readonly star: CelestialBody;
  readonly bodies: CelestialBody[] = [];
  private byId = new Map<string, CelestialBody>();

  constructor(config: SolarSystemConfig = DEFAULT_SYSTEM_CONFIG) {
    this.seed = config.seed;
    this.frames = new ReferenceFrameManager(
      new StaticFrame(ROOT_FRAME_ID, null, 'Cyrene system inertial'),
    );

    // The star sits at the root; it has no orbit of its own.
    this.star = new CelestialBody(config.star, null);
    this.register(this.star, ROOT_FRAME_ID);

    // Bodies may reference parents that appear later in the list, so resolve in
    // dependency order rather than assuming the data file is sorted.
    const pending = [...config.bodies];
    let guard = pending.length * pending.length + 8;
    while (pending.length > 0) {
      if (guard-- < 0) {
        throw new Error(
          `Unresolvable parent references for bodies: ${pending.map((b) => b.id).join(', ')}`,
        );
      }
      const def = pending.shift()!;
      const parent = def.parentId ? this.byId.get(def.parentId) : undefined;
      if (def.parentId && !parent) {
        pending.push(def);
        continue;
      }
      const body = new CelestialBody(def, parent ? parent.mu : null);
      body.parent = parent ?? null;
      parent?.children.push(body);
      this.register(body, parent ? parent.inertialFrameId : ROOT_FRAME_ID);
    }

    for (const body of this.bodies) body.computeSphereOfInfluence();
  }

  private register(body: CelestialBody, parentFrameId: string): void {
    this.bodies.push(body);
    this.byId.set(body.id, body);

    this.frames.register(
      new DynamicFrame(body.inertialFrameId, parentFrameId, `${body.name} (inertial)`, (t) =>
        body.inertialStateAt(t),
      ),
    );
    this.frames.register(
      new DynamicFrame(body.fixedFrameId, body.inertialFrameId, `${body.name} (body-fixed)`, (t) =>
        body.fixedStateAt(t),
      ),
    );
  }

  get(id: string): CelestialBody {
    const b = this.byId.get(id);
    if (!b) throw new Error(`Unknown celestial body "${id}"`);
    return b;
  }

  find(id: string): CelestialBody | undefined {
    return this.byId.get(id);
  }

  /** Id of the root (system inertial) reference frame. */
  get root(): string {
    return ROOT_FRAME_ID;
  }

  get homePlanet(): CelestialBody {
    return this.get(HOME_PLANET_ID);
  }

  /** All bodies that the player can land on and walk around. */
  get explorableBodies(): CelestialBody[] {
    return this.bodies.filter((b) => b.explorable && b.def.terrain !== null);
  }

  get planets(): CelestialBody[] {
    return this.bodies.filter((b) => b.def.kind === 'planet' || b.def.kind === 'gasGiant');
  }

  get moons(): CelestialBody[] {
    return this.bodies.filter((b) => b.def.kind === 'moon');
  }

  /** Position of a body in the root (system inertial) frame. */
  positionOf(body: CelestialBody, t: number): Vec3 {
    return this.frames.stateInRoot(body.inertialFrameId, t).position;
  }

  velocityOf(body: CelestialBody, t: number): Vec3 {
    return this.frames.stateInRoot(body.inertialFrameId, t).velocity;
  }

  /**
   * The body whose gravity dominates at a root-frame position.
   *
   * Used to choose the reference frame for orbit readouts, analytical warp and
   * trajectory prediction. Note this is a *labelling* decision only: the actual
   * gravity integration always sums every significant source, so nothing
   * discontinuous happens when this answer changes.
   */
  dominantBody(positionInRoot: Vec3, t: number): CelestialBody {
    let best = this.star;
    let bestAccel = -Infinity;
    for (const body of this.bodies) {
      const p = this.frames.stateInRoot(body.inertialFrameId, t).position;
      const r = Math.max(body.radius * 0.5, positionInRoot.distanceTo(p));
      const a = body.mu / (r * r);
      if (a > bestAccel) {
        bestAccel = a;
        best = body;
      }
    }
    return best;
  }

  /**
   * Nearest explorable body whose sphere of influence contains the position,
   * or null when in interplanetary space.
   */
  bodyContaining(positionInRoot: Vec3, t: number): CelestialBody | null {
    let best: CelestialBody | null = null;
    let bestRadius = Infinity;
    for (const body of this.bodies) {
      if (body === this.star) continue;
      const p = this.frames.stateInRoot(body.inertialFrameId, t).position;
      const d = positionInRoot.distanceTo(p);
      if (d < body.sphereOfInfluence && body.sphereOfInfluence < bestRadius) {
        best = body;
        bestRadius = body.sphereOfInfluence;
      }
    }
    return best;
  }
}
